// Função auxiliar para conectar usando SIP.js (não JsSIP)
// Usando sip.js 0.7.8 (mesma versão do softphone que funciona)
// A API do sip.js 0.7.8 é diferente - usa SIP.UA em vez de UserAgent
import SIP from "sip.js";
import { v4 as uuidv4 } from "uuid";

export const connectToAsteriskSIP = (config, callbacks) => {
  const { asteriskUrl, extension, password, useInsecureConnection } = config;
  const { onConnected, onDisconnected, onRegistered, onRegistrationFailed, onError, onIncomingCall, onCallProgress, onCallAccepted, onCallEnded, onCallFailed } = callbacks;

  try {
    // A URL já vem normalizada do componente principal (com ws:// ou wss://)
    let url = asteriskUrl.trim();
    const isSecure = url.startsWith('wss://') || (!useInsecureConnection && !url.startsWith('ws://'));
    
    // Validar formato da URL
    if (!url.match(/^(ws|wss):\/\/.+/)) {
      throw new Error(`URL do WebSocket inválida: ${url}. Deve começar com ws:// ou wss://`);
    }
    
    console.log('🔧 URL do WebSocket validada:', url);

    // Extrair domínio e porta do host (ex: ws://pabx.automatize.plus:8089/ws -> pabx.automatize.plus:8089)
    const urlMatch = url.match(/:\/\/([^\/]+)/);
    const hostPort = urlMatch ? urlMatch[1] : url.split('/')[2] || 'localhost';
    const domain = hostPort.split(':')[0];
    const port = hostPort.split(':')[1] || (isSecure ? '443' : '80');
    
    // Gerar instance_id para suporte a outbound (RFC 5626)
    const instanceId = uuidv4();
    
    console.log('🔧 Configurando SIP.js');
    console.log('🔧 Domain:', domain);
    console.log('🔧 WebSocket URL:', url);
    console.log('🔧 Is Secure (wss):', isSecure);
    console.log('🔧 Instance ID:', instanceId);
    
    // Configuração do SIP.js (baseado no softphone que funciona)
    // O wsServers DEVE ser um array de objetos com ws_uri e sip_uri
    // Formato exato do softphone que funciona:
    // [{ ws_uri: "wss://...", sip_uri: "<sip:...;transport=ws;lr>", weight: 0 }]
    
    console.log('🔧 URL do WebSocket antes de configurar:', url);
    console.log('🔧 URL é válida?', url && url.length > 0 && url.startsWith('ws'));
    
    // Validar que a URL não está vazia
    if (!url || url.trim() === '') {
      throw new Error('URL do WebSocket está vazia');
    }
    
    // Extrair porta da URL se existir
    const urlPort = hostPort.includes(':') ? hostPort.split(':')[1] : (isSecure ? '443' : '80');
    
    // Criar objeto wsServer no formato exato do softphone que funciona
    // IMPORTANTE: O sip_uri deve usar transport=ws mesmo quando usando wss://
    // O transport no sip_uri se refere ao protocolo SIP, não ao protocolo WebSocket
    const wsServerConfig = {
      ws_uri: url,
      sip_uri: `<sip:${domain}${urlPort && urlPort !== (isSecure ? '443' : '80') ? ':' + urlPort : ''};transport=ws;lr>`,
      weight: 0,
      status: 0,
      scheme: isSecure ? 'WSS' : 'WS'
    };
    
    console.log('🔧 WebSocket Server Config:', JSON.stringify(wsServerConfig, null, 2));
    console.log('🔧 wsServerConfig.ws_uri:', wsServerConfig.ws_uri);
    console.log('🔧 wsServerConfig.ws_uri type:', typeof wsServerConfig.ws_uri);
    console.log('🔧 wsServerConfig.ws_uri length:', wsServerConfig.ws_uri?.length);
    
    // Validar ws_uri antes de usar
    if (!wsServerConfig.ws_uri || wsServerConfig.ws_uri.trim() === '') {
      throw new Error(`ws_uri está vazio: ${JSON.stringify(wsServerConfig)}`);
    }
    
    const configuration = {
      uri: `sip:${extension}@${domain}`,
      // sip.js 0.7.8 usa 'authorizationUser' e 'password' em vez de 'authorizationUsername' e 'authorizationPassword'
      authorizationUser: extension,
      password: password,
      displayName: extension,
      // WebSocket servers - sip.js 0.7.8 aceita string, array de strings ou array de objetos
      // Baseado nos logs do softphone que funciona, vamos usar array de objetos
      wsServers: [{
        ws_uri: url,
        sip_uri: `<sip:${domain}${urlPort && urlPort !== (isSecure ? '443' : '80') ? ':' + urlPort : ''};transport=ws;lr>`,
        weight: 0
      }],
      // Registrar server
      registrarServer: `sip:${domain}`,
      // Register expires (120 segundos como o softphone que funciona)
      registerExpires: 120,
      // Instance ID para outbound (RFC 5626)
      // sip.js 0.7.8 espera apenas o UUID, não o formato urn:uuid:
      instanceId: instanceId,
      // HACKS importantes do sip.js
      hackWssInTransport: true, // Força transport=wss quando usando wss://
      contactTransport: isSecure ? "wss" : "ws", // Força o transport no Contact
      hackIpInContact: false, // Não precisa, funciona com .invalid
      // Logs para debug
      traceSip: true,
      // Configurações de reconexão
      connectionRecoveryMinInterval: 2,
      connectionRecoveryMaxInterval: 3,
      // Auto start/stop
      autostart: true,
      autostop: true,
    };
    
    console.log('🔧 Configuração SIP.js:', {
      uri: configuration.uri,
      domain: domain,
      instanceId: instanceId,
      url: url,
      wsServers: configuration.wsServers,
      wsServersType: typeof configuration.wsServers,
      wsServersIsArray: Array.isArray(configuration.wsServers),
      hackWssInTransport: configuration.hackWssInTransport,
      contactTransport: configuration.contactTransport,
    });

    // Validar configuração antes de criar UserAgent
    console.log('🔧 Validando configuração antes de criar UserAgent...');
    console.log('🔧 wsServers:', configuration.wsServers);
    console.log('🔧 wsServers type:', typeof configuration.wsServers);
    console.log('🔧 wsServers length:', configuration.wsServers?.length);
    
    if (!configuration.wsServers || (typeof configuration.wsServers === 'string' && configuration.wsServers.trim() === '')) {
      throw new Error(`wsServers está vazio ou inválido: ${JSON.stringify(configuration.wsServers)}`);
    }
    
    if (Array.isArray(configuration.wsServers)) {
      const emptyServers = configuration.wsServers.filter(s => !s || (typeof s === 'string' && s.trim() === '') || (typeof s === 'object' && !s.ws_uri));
      if (emptyServers.length > 0) {
        throw new Error(`wsServers contém entradas vazias: ${JSON.stringify(emptyServers)}`);
      }
    }
    
    console.log('🔧 Configuração validada, criando UserAgent...');
    // sip.js 0.7.8 usa SIP.UA em vez de new UserAgent()
    const userAgent = new SIP.UA(configuration);
    let currentSession = null;

    // Event handlers do UserAgent (sip.js 0.7.8 usa .on() em vez de delegate)
    userAgent.on('connected', () => {
      console.log('✅ SIP.js: Conectado');
      onConnected && onConnected();
    });

    userAgent.on('disconnected', (error) => {
      console.log('❌ SIP.js: Desconectado', error);
      onDisconnected && onDisconnected();
    });

    userAgent.on('message', (message) => {
      console.log('📨 SIP.js: Mensagem recebida', message);
    });

    userAgent.on('invite', (session) => {
      console.log('📞 SIP.js: Chamada recebida!', session);
      currentSession = session;
      onIncomingCall && onIncomingCall(session);
      
      // Event handlers da sessão (sip.js 0.7.8 usa .on() em vez de delegate)
      session.on('progress', () => {
        console.log('📞 Chamada em progresso');
        onCallProgress && onCallProgress(session);
      });

      session.on('accepted', () => {
        console.log('✅ Chamada aceita');
        // Parar ringtone quando chamada é aceita
        onCallAccepted && onCallAccepted(session);
      });

      session.on('bye', () => {
        console.log('📞 Chamada encerrada');
        onCallEnded && onCallEnded();
        currentSession = null;
      });

      session.on('cancel', () => {
        console.log('📞 Chamada cancelada');
        onCallEnded && onCallEnded();
        currentSession = null;
      });
    });

    userAgent.on('registered', () => {
      console.log('✅ SIP.js: Registrado');
      onRegistered && onRegistered();
    });

    userAgent.on('unregistered', () => {
      console.log('⚠️ SIP.js: Desregistrado');
    });

    userAgent.on('registrationFailed', (error) => {
      console.error('❌ SIP.js: Falha no registro', error);
      onRegistrationFailed && onRegistrationFailed(error);
    });

    // Iniciar o UserAgent (sip.js 0.7.8 usa .start() diretamente)
    userAgent.start();

    // Retornar objeto com métodos para controlar
    return {
      userAgent,
      stop: () => {
        if (userAgent) {
          userAgent.stop();
        }
      },
      isRegistered: () => {
        return userAgent ? userAgent.isRegistered() : false;
      },
      getCurrentSession: () => currentSession,
    };
  } catch (error) {
    console.error('❌ Erro ao criar UserAgent SIP.js:', error);
    onError && onError(error);
    return null;
  }
};

export const makeCall = (uaWrapper, number, extension, domain) => {
  if (!uaWrapper || !uaWrapper.isRegistered()) {
    throw new Error('UA não está registrado');
  }

  const userAgent = uaWrapper.userAgent;
  const target = `sip:${number}@${domain}`;
  console.log('📞 Discando para:', target);
  
  // sip.js 0.7.8 usa userAgent.invite() em vez de userAgent.call()
  const options = {
    media: {
      constraints: {
        audio: true,
        video: false,
      },
    },
  };

  console.log('📞 Fazendo chamada com opções:', options);
  
  // No sip.js 0.7.8, usamos userAgent.invite() para fazer chamadas
  const session = userAgent.invite(target, options);
  
  if (!session) {
    throw new Error('Falha ao criar sessão de chamada');
  }
  
  // Event handlers da sessão serão configurados no componente principal
  // para ter acesso aos callbacks e stopRingtone
  return session;
};

export const hangupCall = (session) => {
  if (session) {
    session.bye();
  }
};

export const holdCall = (session) => {
  if (session) {
    session.hold();
  }
};

export const unholdCall = (session) => {
  if (session) {
    session.unhold();
  }
};
